import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@/common/infrastructure/http/apiClient';
import { isNotFoundError } from '@/common/infrastructure/http/problemDetails';
import { toNumberOrNull } from '@/blocks/application/helpers/toNumberOrNull';
import { resolveBlockKind } from '@/blocks/application/helpers/resolveBlockKind';
import { toBlockVersion } from '@/blocks/application/mappers/blockMapper';
import { resolveEditableBlockVersion, resolvePublishedBlockVersion } from '@/blocks/application/hooks/resolveEditableBlockVersion';
import { resolveBlockLifecycleStatus } from '@/blocks/application/sieve/buildBlocksSieveFilter';
import type { BlockStyleDto, BlockVersionDto } from '@/blocks/application/mappers/blockMapper';
import type { Block, BlockKind, BlockVersion } from '@/blocks/domain/types';
import { DEFAULT_BLOCK_STYLE, LEGACY_BLOCK_KIND_ALIASES } from '@/blocks/domain/types';
import { defaultContentForKind } from '@/blocks/domain/helpers/defaultContentForKind';
import { resolveBlockName } from '@/blocks/domain/helpers/resolveBlockName';
import { defaultFieldsFor } from '@/blocks/domain/helpers/defaultFieldsFor';
import { legacyCustomerFieldsFor } from '@/blocks/domain/helpers/legacyCustomerFields';
import { defaultSignatureSections } from '@/blocks/domain/helpers/signatureDefaults';
import { normalizeStyleKeys } from '@/blocks/domain/helpers/styleNormalization';
import type { PdfBlockDetailDto } from '@/generated/models/pdfBlockDetailDto';
import type { PdfBlockVersionDto } from '@/generated/models/pdfBlockVersionDto';

/**
 * Response from `GET /api/pdf-templates/v1/blocks/{id}/detail`, typed by the generated
 * `PdfBlockDetailDto` / `PdfBlockVersionDto` (Orval, `src/generated`). The editor picks the editable
 * draft via `resolveEditableBlockVersion`. Each version arrives with `blockJson` as a *string* — the
 * document-builder needs the structured `content` + `style`, so we parse and remap below.
 *
 * Ids are 64-bit on the wire and arrive as numbers (or numeric strings); every id is normalised to a
 * string here so the domain (`BlockVersion.blockVersionId: string`) compares with `===`.
 */
type PdfBlockDetailResponse = PdfBlockDetailDto;

/** A detail version with ids and optional fields normalised; satisfies `BlockDetailVersionRow`. */
interface WireBlockVersion {
  blockVersionId: string;
  blockId: string;
  versionNumber: number;
  blockJson: string;
  publishedAt: string | null;
  status?: string;
  createdAt: string;
  authorDisplayName: string | null;
  revertedFromVersionNumber: number | null;
}

function normalizeWireVersion(raw: PdfBlockVersionDto, fallbackBlockId: string): WireBlockVersion {
  const author = raw.createdBy?.displayName?.trim();
  return {
    blockVersionId: String(raw.blockVersionId ?? ''),
    blockId: raw.blockId != null ? String(raw.blockId) : fallbackBlockId,
    versionNumber: toNumberOrNull(raw.versionNumber) ?? 0,
    blockJson: raw.blockJson ?? '',
    publishedAt: raw.publishedAt ?? null,
    ...(raw.status !== undefined ? { status: raw.status } : {}),
    createdAt: raw.createdAt ?? '',
    authorDisplayName: author ? author : null,
    revertedFromVersionNumber: toNumberOrNull(raw.revertedFromVersionNumber)
  };
}

interface ParsedBlockJson {
  content?: unknown;
  style?: unknown;
}

function isBlockStyleDtoKey(key: string): key is keyof BlockStyleDto {
  return (
    key === 'fontSize' ||
    key === 'fontSizePt' ||
    key === 'fontWeight' ||
    key === 'fontWeightValue' ||
    key === 'textAlign' ||
    key === 'textColor' ||
    key === 'backgroundColor' ||
    key === 'paddingY' ||
    key === 'borderTop' ||
    key === 'borderBottom' ||
    key === 'italic' ||
    key === 'uppercase' ||
    key === 'previewHeight' ||
    key === 'fontFamily' ||
    key === 'borderColor'
  );
}

/**
 * Style stored in `BlockJson` may use either camelCase (FE-authored) or
 * snake_case (POC-imported / seed). Normalise to the FE-side DTO that
 * `mapStyle` expects so we don't get a sea of `undefined` properties.
 */
function normalizeStyle(style: unknown): BlockStyleDto {
  const src = (style ?? {}) as Record<string, unknown>;
  const normalizedSrc = normalizeStyleKeys(src);
  // Start from defaults so we always return a fully-shaped DTO.
  const out: BlockStyleDto = { ...DEFAULT_BLOCK_STYLE };
  for (const [key, value] of Object.entries(normalizedSrc)) {
    if (!isBlockStyleDtoKey(key)) continue;

    switch (key) {
      case 'fontSize':
        if (typeof value === 'number' && Number.isFinite(value)) out.fontSize = value;
        break;
      case 'fontSizePt':
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) out.fontSizePt = value;
        break;
      case 'fontWeight':
        if (value === 'normal' || value === 'bold') out.fontWeight = value;
        break;
      case 'fontWeightValue':
        if (typeof value === 'number' && Number.isFinite(value) && value >= 100 && value <= 900) out.fontWeightValue = value;
        break;
      case 'textAlign':
        if (value === 'left' || value === 'center' || value === 'right') out.textAlign = value;
        break;
      case 'textColor':
        if (typeof value === 'string') out.textColor = value;
        break;
      case 'backgroundColor':
        if (typeof value === 'string') out.backgroundColor = value;
        break;
      case 'paddingY':
        if (typeof value === 'number' && Number.isFinite(value)) out.paddingY = value;
        break;
      case 'borderTop':
        if (typeof value === 'boolean') out.borderTop = value;
        break;
      case 'borderBottom':
        if (typeof value === 'boolean') out.borderBottom = value;
        break;
      case 'italic':
        if (typeof value === 'boolean') out.italic = value;
        break;
      case 'uppercase':
        if (typeof value === 'boolean') out.uppercase = value;
        break;
      case 'previewHeight':
        if (typeof value === 'number' && Number.isFinite(value)) out.previewHeight = value;
        break;
      case 'fontFamily':
      case 'borderColor':
        if (typeof value === 'string' && value.trim()) out[key] = value.trim();
        break;
      default:
        break;
    }
  }
  return out;
}

function safeParseBlockJson(raw: string): ParsedBlockJson {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as ParsedBlockJson;
  } catch {
    /* ignore — fall through to empty payload */
  }
  return {};
}

/**
 * Reconcile two known shape drifts in seed-authored / POC-imported content:
 *   1. `content.kind === 'prose'` for any of the prose-family blocks
 *      (scope_of_work / terms_conditions / custom_text / comments). Older
 *      seeds shipped the alias; the FE's mapContent dispatcher matches on
 *      the block's actual kind, so the alias hits UnsupportedKindEditor.
 *   2. `content.height: N` for spacers. The FE reads `height_px` (which is
 *      then mapped to `heightPx`); the legacy seed wrote `height`.
 *
 * Both are normalised here so the rest of the pipeline only ever sees the
 * canonical snake_case shape that `mapContent` expects.
 */
const PROSE_KINDS = new Set(['scope_of_work', 'terms_conditions', 'custom_text', 'custom', 'comments']);
const FIELD_KINDS = new Set(['header', 'customer_block', 'signature']);

function normalizeContent(
  content: Record<string, unknown>,
  blockKind: BlockKind,
  documentTypeKey?: string | null
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...content };
  // A legacy `project_details` block may carry its own kind inside the JSON too; mapContent only
  // knows the kind the block is handled as.
  if (typeof next.kind === 'string' && LEGACY_BLOCK_KIND_ALIASES.get(next.kind) === blockKind) {
    next.kind = blockKind;
  }
  if (next.kind === 'prose' && PROSE_KINDS.has(blockKind)) {
    next.kind = blockKind;
  }
  if (blockKind === 'spacer' && next.height_px === undefined && typeof next.height === 'number') {
    next.height_px = next.height;
  }
  // Field-based kinds (header / customer_block / signature) used to ship
  // hard-coded slots like `title_template`, `subsidiary_name`, `prepared_by_*`,
  // `left_label`, etc. We migrated to a flexible `fields: BlockField[]` shape
  // matching the POC. Any block JSON that arrives without `fields` is filled
  // in from the canonical defaults so older seeded blocks keep mounting.
  if (FIELD_KINDS.has(blockKind) && !Array.isArray(next.fields)) {
    // A stored customer block with no fields keeps the flat rows it always got, not a NEW block's sectioned rows (LLAMS-844).
    const defaults =
      blockKind === 'customer_block'
        ? legacyCustomerFieldsFor(documentTypeKey)
        : defaultFieldsFor(blockKind as 'header' | 'signature', documentTypeKey);
    next.fields = defaults.map((f) => ({
      id: f.id,
      label: f.label,
      value: f.value,
      width: f.width,
      align: f.align,
      style: f.style,
      visible: f.visible,
      sort_order: f.sortOrder,
      ...(f.name !== undefined ? { name: f.name } : {}),
      ...(f.sectionId !== undefined ? { section_id: f.sectionId } : {}),
      ...(f.column !== undefined ? { column: f.column } : {})
    }));
    // A signature's default fields sit in sections of their own (the statement, the 2-column lines).
    const sections = blockKind === 'signature' && next.sections === undefined ? defaultSignatureSections(defaults) : null;
    if (sections) next.sections = sections;
  }
  return next;
}

function hydrateVersion(
  raw: WireBlockVersion,
  blockKind: BlockKind,
  documentTypeKey?: string | null,
  isSupportedKind = true
): BlockVersion {
  const parsed = safeParseBlockJson(raw.blockJson);
  const metadata = {
    ...(raw.status !== undefined ? { status: raw.status } : {}),
    authorDisplayName: raw.authorDisplayName,
    revertedFromVersionNumber: raw.revertedFromVersionNumber
  };
  // An UNSUPPORTED kind stops here, before `mapContent`: the app has no mapper for it, and mapping its
  // JSON as some other kind would show (and could re-save) the wrong content. The placeholder content
  // is never rendered: the editor opens read-only on `UnsupportedKindEditor` and the card paints no preview.
  if (!isSupportedKind) {
    return {
      blockVersionId: raw.blockVersionId,
      blockId: raw.blockId,
      versionNumber: raw.versionNumber,
      content: defaultContentForKind(blockKind, documentTypeKey),
      style: DEFAULT_BLOCK_STYLE,
      blockJson: parsed,
      publishedAt: raw.publishedAt,
      createdAt: raw.createdAt,
      ...metadata
    };
  }
  // Newly-created versions historically seeded BlockJson="{}" (no content).
  // mapContent's exhaustive switch would throw on the undefined branch —
  // short-circuit by returning a kind-defaulted version so the editor mounts.
  // Publish is still required until a renderable snapshot is persisted.
  if (!parsed.content || typeof parsed.content !== 'object') {
    return {
      blockVersionId: raw.blockVersionId,
      blockId: raw.blockId,
      versionNumber: raw.versionNumber,
      content: defaultContentForKind(blockKind, documentTypeKey),
      style: DEFAULT_BLOCK_STYLE,
      blockJson: parsed,
      publishedAt: raw.publishedAt,
      createdAt: raw.createdAt,
      ...metadata
    };
  }
  const normalizedContent = normalizeContent(parsed.content as Record<string, unknown>, blockKind, documentTypeKey);
  const dto: BlockVersionDto = {
    blockVersionId: raw.blockVersionId,
    blockId: raw.blockId,
    versionNumber: raw.versionNumber,
    // `mapContent` reads snake_case keys, which is how both the seed data and
    // the FE's own save payload write block content — leave it as-is.
    content: normalizedContent as BlockVersionDto['content'],
    style: normalizeStyle(parsed.style),
    blockJson: parsed,
    publishedAt: raw.publishedAt,
    createdAt: raw.createdAt,
    ...metadata
  };
  return toBlockVersion(dto);
}

export interface UseBlockOptions {
  /**
   * ANDed with `!!blockId`. Lets a caller gate the detail fetch on a signal of its own — e.g. a
   * Block Library card gating on `useInViewOnce` so a gallery of N cards fires at most N detail
   * fetches spread over scroll, not N fetches on first paint (LLAMS-617, `BlockThumbnail.tsx`).
   * Defaults to `true` (unchanged behaviour for every other caller).
   */
  enabled?: boolean;
}

export const useBlock = (blockId: string | null, documentTypeKey?: string | null, options: UseBlockOptions = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['document-builder', 'block', blockId] as const,
    enabled: !!blockId && enabled,
    queryFn: async () => {
      if (!blockId) throw new Error('Missing blockId');

      const detail = await customInstance<PdfBlockDetailResponse>({
        url: `/api/pdf-templates/v1/blocks/${blockId}/detail`,
        method: 'GET'
      });

      const versions = (Array.isArray(detail.versions) ? detail.versions : []).map((version) => normalizeWireVersion(version, blockId));
      const lifecycleStatus = resolveBlockLifecycleStatus(detail);
      const resolution = {
        ...detail,
        status: lifecycleStatus === 'Unknown' ? (detail.status ?? '') : lifecycleStatus,
        currentVersion: toNumberOrNull(detail.currentVersion) ?? 0
      };
      const { kind, rawKind, isSupportedKind } = resolveBlockKind(detail.kind ?? detail.blockType);
      // An unsupported kind is read-only: there is no editable version, only the published one to show.
      const editableTarget = isSupportedKind ? resolveEditableBlockVersion(resolution, versions) : null;
      const publishedTarget = resolvePublishedBlockVersion(resolution, versions);

      const block: Block = {
        blockId: String(detail.blockId ?? blockId),
        kind,
        rawKind,
        isSupportedKind,
        name: resolveBlockName(detail.name, { kind, rawKind, isSupportedKind }),
        description: detail.description ?? null,
        isActive: detail.isActive ?? lifecycleStatus !== 'Archived',
        status: lifecycleStatus,
        createdAt: detail.createdAt ?? '',
        updatedAt: detail.updatedAt ?? '',
        currentVersionId: publishedTarget?.blockVersionId ?? null,
        draftVersionId: detail.draftVersionId != null && String(detail.draftVersionId).trim() !== '' ? String(detail.draftVersionId) : null,
        // ptb serialises `draftVersionId: null` when there is no draft, so a missing KEY (not a null
        // value) means the API predates PDF-1 and would publish on every Save.
        draftsSupported: 'draftVersionId' in detail,
        currentVersion: toNumberOrNull(detail.currentVersion) ?? 0
      };

      const editableRaw = editableTarget
        ? (versions.find((version) => String(version.blockVersionId) === String(editableTarget.blockVersionId)) ?? null)
        : null;
      const publishedRaw = publishedTarget
        ? (versions.find((version) => String(version.blockVersionId) === String(publishedTarget.blockVersionId)) ?? null)
        : null;

      const currentVersion: BlockVersion | null = editableRaw
        ? hydrateVersion(editableRaw, block.kind, documentTypeKey, isSupportedKind)
        : null;
      const publishedVersion: BlockVersion | null = publishedRaw
        ? hydrateVersion(publishedRaw, block.kind, documentTypeKey, isSupportedKind)
        : null;
      const hydratedVersions = versions
        .map((rawVersion) => hydrateVersion(rawVersion, block.kind, documentTypeKey, isSupportedKind))
        .sort((a, b) => b.versionNumber - a.versionNumber);

      return { block, currentVersion, publishedVersion, versions: hydratedVersions };
    },
    staleTime: 1000 * 60,
    // A 404 is final (deleted, archived away or another organization's block): fail at once so the editor
    // can say "Block not found" instead of spinning through the default retries (6-12 s). Other errors retry once.
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 1,
    refetchOnMount: 'always'
  });
};
